import {Button} from '@/components/ui/button';
import {Input} from '@/components/ui/input';
import {Modal} from '@/components/ui/modal';
import {
  Select,
  SelectItem,
  SelectPopup,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {toastManager} from '@/components/ui/toast';
import {createEventList} from '@/server/savedEvent';
import {toTitleCase} from '@/utils/displayFormatUtils';
import {useRouter} from '@tanstack/react-router';
import {useState} from 'react';

interface CreateListModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called after a list is successfully created, with the new list's id and name. */
  onCreated?: (list: {id: string; name: string}) => void;
}

const visibilityOptions = [
  {value: 'PRIVATE', label: 'Private'},
  {value: 'PUBLIC', label: 'Public'},
];

/**
 * Modal dialog for creating a new event list.
 * Uses Modal for the shell with name input and visibility select.
 */
function CreateListModal({open, onOpenChange, onCreated}: CreateListModalProps) {
  const router = useRouter();
  const [name, setName] = useState('');
  const [visibility, setVisibility] = useState<'PRIVATE' | 'PUBLIC'>('PRIVATE');
  const [loading, setLoading] = useState(false);

  function resetForm() {
    setName('');
    setVisibility('PRIVATE');
  }

  async function handleSubmit() {
    if (!name.trim()) return;
    setLoading(true);
    try {
      const newList = await createEventList({data: {name: name.trim(), visibility}});
      toastManager.add({title: 'List created', type: 'success'});
      onOpenChange(false);
      resetForm();
      onCreated?.(newList);
      router.invalidate();
    } catch (err) {
      toastManager.add({
        title: err instanceof Error ? err.message : 'Failed to create list',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  }

  return (
    <Modal
      open={open}
      onOpenChange={(nextOpen) => {
        onOpenChange(nextOpen);
        if (!nextOpen) resetForm();
      }}
      title="Create List"
    >
      <p className="text-sm text-gray-500 mb-4">
        Create a new event list to organize your saved events.
      </p>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit();
        }}
      >
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="list-name" className="text-sm font-medium">
              Name
            </label>
            <Input
              id="list-name"
              placeholder="My list"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={100}
              required
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium">Visibility</label>
            <Select
              value={visibility}
              onValueChange={(value) => {
                if (value === 'PRIVATE' || value === 'PUBLIC') {
                  setVisibility(value);
                }
              }}
            >
              <SelectTrigger>
                <SelectValue>{toTitleCase(visibility)}</SelectValue>
              </SelectTrigger>
              <SelectPopup>
                {visibilityOptions.map((opt) => (
                  <SelectItem
                    key={opt.value}
                    value={opt.value}
                    label={opt.label}
                  >
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectPopup>
            </Select>
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <Button
            variant="outline"
            type="button"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={loading || !name.trim()}>
            {loading ? 'Creating...' : 'Create'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

export {CreateListModal};
